/* eslint-disable no-useless-escape */
/* eslint-disable no-console */
/**
 * Utilities for guessing whether a browser is compatible with our Web applications.
 */
class BrowserCompatibilityHeuristics {
  /**
   * Checks if browser is unsupported based on User-Agent string.
   * Intended to be run server-side (e.g., in middleware) before React loads.
   *
   * @param userAgent - The User-Agent header string from the request
   * @returns {boolean} True if the browser is unsupported, false otherwise.
   */
  static isUnsupportedBrowser(userAgent: string): boolean {
    // Allow known bots/crawlers through (for SEO and social media link previews)
    if (
      /bot|crawl|spider|facebookexternalhit|Facebot|Twitterbot|LinkedInBot|Googlebot|Google-PageRenderer|Google-InspectionTool|Bingbot|Slurp|DuckDuckBot|Applebot/i.test(
        userAgent
      )
    ) {
      return false;
    }

    // Internet Explorer (any version)
    if (/MSIE|Trident/.test(userAgent)) {
      return true;
    }

    // Very old Safari (iOS < 15.4, Safari < 15.4)
    // Skip this check if Chrome is in the UA - Android WebViews include "Version/4.0"
    // but use modern Chrome rendering, so we should check Chrome version instead
    const safariMatch = userAgent.match(/Version\/(\d+)\.(\d+)/);
    if (safariMatch && !/Chrome/.test(userAgent)) {
      const major = parseInt(safariMatch[1], 10);
      const minor = parseInt(safariMatch[2], 10);
      if (major < 15 || (major === 15 && minor < 4)) {
        return true;
      }
    }

    // iOS < 15.4 (OS 15_4 format)
    const iosMatch = userAgent.match(/OS (\d+)_(\d+)/);
    if (iosMatch && /iPad|iPhone|iPod/.test(userAgent)) {
      const major = parseInt(iosMatch[1], 10);
      const minor = parseInt(iosMatch[2], 10);
      if (major < 15 || (major === 15 && minor < 4)) {
        return true;
      }
    }

    // Check Edge first (before Chrome, since Edge includes "Chrome/" in User-Agent)
    // Edge uses "Edg/" in User-Agent
    const edgeMatch = userAgent.match(/Edg\/(\d+)/);
    if (edgeMatch) {
      const version = parseInt(edgeMatch[1], 10);
      // Edge is supported if version >= 98 (same as Chrome)
      if (version < 98) {
        return true;
      }
      // Edge is supported, skip Chrome check
      return false;
    }

    // Check Opera first (before Chrome, since Opera includes "Chrome/" in User-Agent)
    // Opera uses "OPR/" in User-Agent
    const operaMatch = userAgent.match(/OPR\/(\d+)/);
    if (operaMatch) {
      const version = parseInt(operaMatch[1], 10);
      // Opera is supported if version >= 83 (Opera's versioning differs from Chrome)
      if (version < 83) {
        return true;
      }
      // Opera is supported, skip Chrome check
      return false;
    }

    // Very old Chrome (< 98)
    // Only check Chrome if it's not Edge or Opera (both include "Chrome/" in User-Agent)
    const chromeMatch = userAgent.match(/Chrome\/(\d+)/);
    if (chromeMatch && !/Edg|OPR/.test(userAgent)) {
      const version = parseInt(chromeMatch[1], 10);
      if (version < 98) {
        return true;
      }
    }

    // Very old Firefox (< 94)
    const firefoxMatch = userAgent.match(/Firefox\/(\d+)/);
    if (firefoxMatch) {
      const version = parseInt(firefoxMatch[1], 10);
      if (version < 94) {
        return true;
      }
    }

    return false;
  }

  /**
   * Checks if a pathname should skip browser compatibility checks.
   * Used in middleware to avoid checking static assets, API routes, etc.
   *
   * @param pathname - The pathname from the request URL
   * @returns {boolean} True if the path should skip browser checks, false otherwise.
   */
  static shouldSkipBrowserCheck(pathname: string): boolean {
    return (
      pathname.startsWith("/unsupported-browser") ||
      pathname.startsWith("/_next") ||
      pathname.startsWith("/api") ||
      pathname.match(
        /\.(ico|png|jpg|jpeg|svg|gif|webp|woff|woff2|ttf|eot)$/i
      ) !== null
    );
  }

  /**
   * Determines if an error is likely caused by an outdated browser.
   *
   * @param {Error} error - The error object caught.
   * @returns {boolean} True if the error is likely due to an outdated browser, false otherwise.
   */
  static isErrorDueToOutdatedBrowser(error: Error) {
    const outdatedBrowserErrors = [
      /Promise is not defined/,
      /Map is not defined/,
      /Set is not defined/,
      /BroadcastChannel is not defined/,
      /structuredClone is not defined/,
    ];

    return outdatedBrowserErrors.some((pattern) => pattern.test(error.message));
  }
}

export default BrowserCompatibilityHeuristics;
